import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { apiErrorMessage, formatList } from '../i18n';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { DesignRequestCard } from './designer/DesignRequestCard';
import {
  NewDesignRequestCard,
  type NewDesignDraft,
} from './designer/NewDesignRequestCard';

/**
 * Design service — request briefs, review proofs, approve or ask for changes
 * (Ghost Screen Designer.dc.html).
 */
export function DesignerPage() {
  const { t } = useTranslation('designer');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  };

  const orgQuery = useQuery({
    queryKey: ['organisation', organisationId],
    queryFn: () => api.getOrganisation(organisationId),
  });
  const requestsQuery = useQuery({
    queryKey: ['design-requests', organisationId],
    queryFn: () => api.listDesignRequests(organisationId),
  });
  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
  });
  const mediaQuery = useQuery({
    queryKey: ['media', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
  });

  const retry = useQueryRetry(async () => {
    await Promise.all([
      orgQuery.refetch(),
      requestsQuery.refetch(),
      screensQuery.refetch(),
      mediaQuery.refetch(),
    ]);
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['design-requests', organisationId] });
  };

  const create = useMutation({
    mutationFn: (draft: NewDesignDraft) => api.createDesignRequest(organisationId, draft),
    onSuccess: async () => {
      setFormError(null);
      await invalidate();
      showToast(t('page.toasts.sent'));
    },
    onError: (error) => setFormError(apiErrorMessage(error, t('page.errors.send'))),
  });

  const approve = useMutation({
    mutationFn: (designRequestId: string) =>
      api.approveDesignRequest(organisationId, designRequestId),
    onMutate: (id) => setActionId(id),
    onSuccess: async (request) => {
      await invalidate();
      const names = request.screenIds
        .map((id) => screensQuery.data?.find((screen) => screen.id === id)?.name)
        .filter((name): name is string => Boolean(name));
      showToast(
        names.length
          ? t('page.toasts.approvedOnScreens', { screens: formatList(names) })
          : t('page.toasts.approved'),
      );
    },
    onSettled: () => setActionId(null),
    onError: (error) => showToast(apiErrorMessage(error, t('page.errors.approve'))),
  });

  const changes = useMutation({
    mutationFn: (input: { id: string; changeRequest: string }) =>
      api.requestDesignChanges(organisationId, input.id, {
        changeRequest: input.changeRequest,
      }),
    onMutate: (input) => setActionId(input.id),
    onSuccess: async () => {
      await invalidate();
      showToast(t('page.toasts.changesSent'));
    },
    onSettled: () => setActionId(null),
    onError: (error) => showToast(apiErrorMessage(error, t('page.errors.changes'))),
  });

  const brand = orgQuery.data?.name || t('page.brandFallback');
  const requests = requestsQuery.data ?? [];
  const screens = screensQuery.data ?? [];
  const mediaById = useMemo(() => {
    const map = new Map(
      (mediaQuery.data ?? []).map((asset) => [asset.id, asset.previewUrl ?? null]),
    );
    return map;
  }, [mediaQuery.data]);

  const screenName = useMemo(() => {
    const map = new Map(screens.map((screen) => [screen.id, screen.name]));
    return (ids: string[]) =>
      ids.map((id) => map.get(id)).filter((name): name is string => Boolean(name));
  }, [screens]);

  const loading =
    requestsQuery.isPending || screensQuery.isPending || mediaQuery.isPending;
  const failed = requestsQuery.isError || screensQuery.isError;

  return (
    <div
      className="grid items-start gap-5"
      style={{
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
        animation: 'gs-in 260ms ease both',
      }}
    >
      {failed || retry.isRetrying ? (
        <QueryErrorState
          message={t('page.loadError')}
          onRetry={retry.retry}
          isRetrying={retry.isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock />
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <NewDesignRequestCard
              organisationId={organisationId}
              screens={screens}
              busy={create.isPending}
              onSubmit={(draft) => create.mutate(draft)}
            />
            {formError ? (
              <p className="m-0 text-sm font-semibold" style={{ color: 'var(--gs-bad)' }}>
                {formError}
              </p>
            ) : null}
          </div>

          <section className="flex flex-col gap-3.5">
            <span className="text-[15px] font-extrabold tracking-[-0.01em]">
              {t('page.requestsTitle')}
            </span>
            {requests.length === 0 ? (
              <div
                className="rounded-[14px] border-[1.5px] border-dashed px-[22px] py-8 text-center text-sm"
                style={{ borderColor: 'var(--gs-line)', color: 'var(--gs-muted)' }}
              >
                {t('page.requestsEmpty')}
              </div>
            ) : (
              requests.map((request) => {
                const photoId = request.proof?.photoMediaAssetId ?? null;
                return (
                  <DesignRequestCard
                    key={request.id}
                    request={request}
                    brand={brand}
                    screenNames={screenName(request.screenIds)}
                    photoUrl={photoId ? (mediaById.get(photoId) ?? null) : null}
                    busy={actionId === request.id}
                    onApprove={() => approve.mutate(request.id)}
                    onRequestChanges={(changeRequest) =>
                      changes.mutate({ id: request.id, changeRequest })
                    }
                  />
                );
              })
            )}
          </section>
        </>
      )}

      {toast ? (
        <div
          className="fixed bottom-4 left-4 right-4 z-[60] flex items-center justify-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-semibold shadow-[0_16px_40px_rgba(0,0,0,0.35)] sm:bottom-7 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:px-[18px]"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
            animation: 'gs-in 200ms ease both',
          }}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: 'var(--gs-ok)' }} />
          {toast}
        </div>
      ) : null}
    </div>
  );
}
